import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Car, 
  User,
  Calendar,
  Clock,
  Phone,
  MapPin,
  CreditCard,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { apiService } from '../../services/api';

export const MotosAlugadasPage = () => {
  const [locacoesAtivas, setLocacoesAtivas] = useState([]);
  const [motos, setMotos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [locacoesData, motosData, clientesData] = await Promise.all([
        apiService.getLocacoes(),
        apiService.getMotos(),
        apiService.getClientes()
      ]);
      
      // Filtrar apenas locações ativas
      const ativas = locacoesData.filter(l => l.status === 'ativa');
      setLocacoesAtivas(ativas);
      setMotos(motosData);
      setClientes(clientesData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMotoInfo = (motoId) => {
    return motos.find(m => m.id === motoId);
  };

  const getClienteInfo = (clienteId) => {
    return clientes.find(c => c.id === clienteId);
  };

  const getPlanoText = (plano) => {
    switch (plano) {
      case 'mensal': return 'Mensal (3 meses)';
      case 'anual': return 'Anual (12 meses)';
      case 'fidelidade': return 'Fidelidade (36 meses)';
      default: return plano;
    }
  };

  const getPlanoDescription = (plano) => {
    switch (plano) {
      case 'mensal': return 'R$ 57,14/dia • Carência 3 meses • Troca de óleo inclusa';
      case 'anual': return 'R$ 50,00/dia • Carência 1 ano • Troca de óleo inclusa';
      case 'fidelidade': return 'R$ 60,71/dia • Tudo incluso • Moto Zero Km';
      default: return plano;
    }
  };

  const getPlanoColor = (plano) => {
    switch (plano) {
      case 'mensal': return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
      case 'anual': return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30';
      case 'fidelidade': return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
      default: return 'bg-slate-700/50 text-gray-300 border border-white/10';
    }
  };

  const calculateDaysRemaining = (dataFim) => {
    const hoje = new Date();
    const fim = new Date(dataFim);
    const diffTime = fim - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateProgress = (dataInicio, dataFim) => {
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    const hoje = new Date();
    
    const totalDays = Math.ceil((fim - inicio) / (1000 * 60 * 60 * 24));
    const passedDays = Math.ceil((hoje - inicio) / (1000 * 60 * 60 * 24));
    
    return Math.min(Math.max((passedDays / totalDays) * 100, 0), 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatPhone = (phone) => {
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const getMotoImage = (cor) => {
    const images = {
      'Vermelha': 'https://customer-assets.emergentagent.com/job_motolocadora/artifacts/15bxt961_IMG_2395.png',
      'Azul': 'https://customer-assets.emergentagent.com/job_motolocadora/artifacts/jpdtlzi1_IMG_2396.png',
      'Preta': 'https://customer-assets.emergentagent.com/job_motolocadora/artifacts/zdx3qkt8_IMG_2397.jpeg'
    };
    return images[cor] || images['Vermelha'];
  };

  if (loading) {
    return <div className="flex justify-center p-8">Carregando motos alugadas...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">
          Motos Alugadas ({locacoesAtivas.length})
        </h2>
      </div>

      {locacoesAtivas.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">
              Nenhuma moto alugada no momento
            </h3>
            <p className="text-gray-400">
              Quando houver locações ativas, elas aparecerão aqui
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {locacoesAtivas.map((locacao) => {
            const moto = getMotoInfo(locacao.moto_id);
            const cliente = getClienteInfo(locacao.cliente_id);
            const diasRestantes = calculateDaysRemaining(locacao.data_fim);
            const progresso = calculateProgress(locacao.data_inicio, locacao.data_fim);
            
            return (
              <Card key={locacao.id} className="overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">
                        Locação #{locacao.id.slice(-8)}
                      </CardTitle>
                      <p className="text-sm text-gray-300 mt-1">
                        {getPlanoDescription(locacao.plano)}
                      </p>
                    </div>
                    <Badge className={getPlanoColor(locacao.plano)}>
                      {getPlanoText(locacao.plano)}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Informações da Moto com Foto */}
                  <div className="flex items-center space-x-4 p-4 bg-slate-800/50 rounded-lg">
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={getMotoImage(moto?.cor)}
                        alt={`DK 160 ${moto?.cor}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Car className="h-4 w-4 text-gray-400" />
                        <span className="font-semibold">
                          {moto?.placa} - DK 160 {moto?.cor}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300">
                        KM Inicial: {locacao.km_inicial ? locacao.km_inicial.toLocaleString() : 'N/A'}
                      </p>
                      <p className="text-sm text-gray-300">
                        KM Atual: {moto?.km_atual ? moto.km_atual.toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Informações do Cliente */}
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div className="flex items-center space-x-2 mb-3">
                      <User className="h-4 w-4 text-blue-400" />
                      <span className="font-semibold text-blue-300">
                        {cliente?.nome}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <Phone className="h-3 w-3 text-gray-400" />
                        <span className="text-gray-300">{formatPhone(cliente?.telefone || '')}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <CreditCard className="h-3 w-3 text-gray-400" />
                        <span className="text-gray-300">CNH: {cliente?.cnh}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-3 w-3 text-gray-400" />
                        <span className="text-gray-300">{cliente?.cidade}</span>
                      </div>
                    </div>
                  </div>

                  {/* Progresso do Contrato */}
                  <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-emerald-300">
                        Progresso do Contrato
                      </span>
                      <span className="text-sm text-emerald-400">
                        {Math.round(progresso)}%
                      </span>
                    </div>
                    
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
                      <div 
                        className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progresso}%` }}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        <div>
                          <p className="text-gray-300">Início</p>
                          <p className="font-medium">{formatDate(locacao.data_inicio)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        <div>
                          <p className="text-gray-300">Término</p>
                          <p className="font-medium">{formatDate(locacao.data_fim)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {diasRestantes > 7 ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : diasRestantes > 0 ? (
                          <Clock className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`text-sm font-medium ${
                          diasRestantes > 7 ? 'text-emerald-400' : 
                          diasRestantes > 0 ? 'text-yellow-600' : 
                          'text-rose-400'
                        }`}>
                          {diasRestantes > 0 
                            ? `${diasRestantes} dias restantes`
                            : diasRestantes === 0 
                            ? 'Vence hoje!'
                            : `${Math.abs(diasRestantes)} dias vencido`
                          }
                        </span>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-400">
                          R$ {locacao.valor_total?.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </p>
                        <p className="text-xs text-gray-400">Valor total</p>
                      </div>
                    </div>
                  </div>

                  {/* Alertas de Manutenção */}
                  {moto && (moto.km_atual - (moto.km_ultima_manutencao || 0)) >= 1200 && (
                    <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-4 w-4 text-orange-400" />
                        <span className="text-sm font-medium text-orange-300">
                          {(moto.km_atual - (moto.km_ultima_manutencao || 0)) >= 1300 
                            ? 'TROCA DE ÓLEO VENCIDA!'
                            : 'Troca de óleo em breve'
                          }
                        </span>
                      </div>
                      <p className="text-xs text-orange-400 mt-1">
                        {moto.km_atual - (moto.km_ultima_manutencao || 0)} km desde última troca
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};